
import SocketMobileScanComponent from '@/components/socketMobile/ScanComponent.vue'
import { getBarcode } from '@/utils/api'
import {
  BARCODE_SCAN,
  CAMERA_SCAN_ROI_THRESHOLD,
  FAIL_SOUND,
  QRCODE_BARCODE_TYPE_REGEX,
  SUCCESS_SOUND,
} from '@/utils/constants'
import errorHandler from '@/utils/errorHandler'
import {
  addLabelToBarcode,
  checkTargetAlertNotifications,
  displayTargetAlert,
  openMessage,
  promptVideoPermission,
} from '@/utils/helpers'
import { BrowserMultiFormatReader, Result } from '@zxing/library'
import { Howl } from 'howler'
import { cloneDeep } from 'lodash'
import { Barcode, BarcodeCapture } from 'scandit-web-datacapture-barcode'
import { AVAILABLE_DELIMITERS, EBCRType } from 'smartbarcode-web-core/src/utils/enums'
import { isEmpty } from 'smartbarcode-web-core/src/utils/typeChecker'
import {
  IAPIErrorCode,
  IBarcode,
  IBarcodeDefinitionType,
  IDisplayBarcodes,
  TError,
} from 'smartbarcode-web-core/src/utils/types/index'
import { mixins, Options } from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import BarcodeReaderMixin from './BarcodeReaderMixin.vue'

interface IScanSound {
  play: Function
  once: Function
}
@Options({
  components: {
    SocketMobileScanComponent,
  },
  name: 'MultipleBarcodeScanMixin',
})
export default class MultipleBarcodeScanMixin extends mixins(BarcodeReaderMixin) {
  scannedBarcodeList: IBarcode[] = []
  barcodesWithNotifications: Record<string, string> = {}
  barcodesLoadingNotifications: Record<string, boolean> = {}
  isDisplayingAlert = false
  dialogVisible = false
  pendingSubmit = false
  currentNotificationBarcode: IDisplayBarcodes | null = null
  isAllowToLeave = false
  scannedBarcode = ''
  video: HTMLVideoElement | null = null
  _barcodeDataCapture: BarcodeCapture | undefined
  barcodes = [] as IBarcode[]
  pendingRequestBarcodeIds: Set<string> = new Set()
  valueCode = ''
  multipleScan = false
  isHandleMultipleScan = false
  isPlayErrorSound = false

  showMultipleScan = false
  isForcedHideMultipleScan = false
  delimiterValue = AVAILABLE_DELIMITERS[0]

  get isShowMultipleScan() {
    this.showMultipleScan =
      this.$store.state.project?.details?.cameraFeatures?.bulkCameraScanResultSetting?.isEnable ?? false
    return this.showMultipleScan
  }

  get delimiter() {
    this.delimiterValue =
      this.$store.state.project?.details?.cameraFeatures?.bulkCameraScanResultSetting?.delimiter ??
      AVAILABLE_DELIMITERS[0]
    return this.delimiterValue
  }

  get displayScannedBarcodes(): IDisplayBarcodes[] {
    const barcodes = cloneDeep(this.scannedBarcodeList) as IDisplayBarcodes[]
    addLabelToBarcode(barcodes, this.project)
    return barcodes
  }

  get currentNotificationMessage(): string {
    return this.currentNotificationBarcode
      ? this.barcodesWithNotifications[this.currentNotificationBarcode.id] || ''
      : ''
  }

  successScanSound: IScanSound = new Howl({
    src: [SUCCESS_SOUND],
    // Try to play again when sound is not ready
    onplayerror: (soundId: string, errMessage: string) => {
      this.successScanSound.once('unlock', () => this.successScanSound.play())
    },
  })

  failScanSound: IScanSound = new Howl({
    src: [FAIL_SOUND],
    // Try to play again when sound is not ready
    onplayerror: (soundId: string, errMessage: string) => {
      this.failScanSound.once('unlock', () => this.failScanSound.play())
    },
  })

  codeReader: BrowserMultiFormatReader | null = new BrowserMultiFormatReader(
    undefined,
    BARCODE_SCAN.timeBetweenScansMillis as number
  )

  pictureWidth = 320
  pictureHeight = 320

  defaultScanReader(result: Result, codeReader: BrowserMultiFormatReader) {
    this.codeReader = codeReader
    if (!result) return

    const temp: { [key: string]: unknown } = { ...result }
    if (!this.isForcedHideMultipleScan && this.isShowMultipleScan && this.isMultipleScan) {
      this.handleMultipleScan((temp.text as string) || '')
      return
    }
    this.handleGotScanBarcode((temp.text as string) || '')
  }

  scanditDataResult(symbology: Barcode) {
    if (!symbology) {
      // TODO: Do something when scanResult unavailable
    }
    if (!this.isForcedHideMultipleScan && this.isShowMultipleScan && this.isMultipleScan) {
      this.handleMultipleScan((symbology?.data as string) || '')
      return
    }
    this.handleGotScanBarcode((symbology?.data as string) || '')
  }

  async barcodeDetectorPolyFillScanReader(result: DetectedBarcode[]) {
    this.isReadyToScan = true
    if (result.length < 1) return
    const filteredScanROI = result.filter((b) => this.isInsideScanROI(b))
    if (filteredScanROI.length < 1) return
    const firstDetectQrcode = filteredScanROI[0]?.rawValue || ''
    if (!this.isForcedHideMultipleScan && this.isShowMultipleScan && this.isMultipleScan) {
      this.handleMultipleScan(firstDetectQrcode)
      return
    }
    filteredScanROI.forEach((barcode) => {
      this.handleGotScanBarcode(barcode.rawValue)
    })
  }

  isInsideScanROI(barcode: DetectedBarcode): boolean {
    const video = document.getElementById('video') as HTMLVideoElement
    if (!video || !video.videoWidth || !video.videoHeight) return true

    const container = document.querySelector('.barcode-scanner') as HTMLElement
    if (!container) return true

    const containerW = container.clientWidth
    const containerH = container.clientHeight

    // object-fit: cover — largerRatio maps between container pixels and video stream pixels
    const largerRatio = Math.max(containerW / video.videoWidth, containerH / video.videoHeight)

    const roi = {
      left: video.videoWidth / 2 - 100 / largerRatio,
      top: video.videoHeight / 2 - 150 / largerRatio,
      right: video.videoWidth / 2 + 100 / largerRatio,
      bottom: video.videoHeight / 2 + 50 / largerRatio,
    }

    const bb = barcode.boundingBox
    const iw = Math.max(0, Math.min(roi.right, bb.right) - Math.max(roi.left, bb.left))
    const ih = Math.max(0, Math.min(roi.bottom, bb.bottom) - Math.max(roi.top, bb.top))
    const barcodeArea = bb.width * bb.height
    if (barcodeArea === 0) return false

    return (iw * ih) / barcodeArea >= CAMERA_SCAN_ROI_THRESHOLD
  }

  async handleMultipleScan(plainText: string) {
    if (this.isHandleMultipleScan) {
      return
    }
    this.isHandleMultipleScan = true
    this.$confirm(this.$t('confirm_scanned_value', { item: plainText }), this.$t('info'), {
      confirmButtonText: this.$t('confirm'),
      confirmButtonClass: 'danger',
      cancelButtonText: this.$t('cancel'),
      closeOnClickModal: true,
      showCancelButton: true,
    })
      .then(() => {
        this.valueCode = this.processValueCode(plainText)
      })
      .finally(() => {
        this.isHandleMultipleScan = false
      })
  }

  async setupVideo() {
    if (!this.video) {
      this.video = document.getElementById('video') as HTMLVideoElement
    } else {
      this.video.srcObject = null
    }
    if (this.selectedCamera) {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            ...(this.barcodeReaderType === EBCRType.BARCODE_DETECTION
              ? { width: { ideal: 3840, max: 4096 }, height: { ideal: 2160, max: 2160 }, frameRate: { ideal: 30, max: 60 } }
              : { width: { ideal: 1000 }, height: { ideal: 1000 }, aspectRatio: 1 }),
            deviceId: { exact: this.selectedCamera },
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
    } else {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            facingMode: 'environment',
            ...(this.barcodeReaderType === EBCRType.BARCODE_DETECTION
              ? { width: { ideal: 3840 }, height: { ideal: 2160 }, frameRate: { ideal: 30, max: 60 } }
              : { aspectRatio: 1 }),
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
    }
  }

  async mounted() {
    this.componentMounted = true
    await this.initCameraSelection()
    this.setupCameraSetting()
  }

  async setupCameraSetting() {
    try {
      this.isReadyToScan = false
      switch (this.barcodeReaderType as EBCRType) {
        case EBCRType.SCANDIT:
          await this.initScanditReader()
          break
        case EBCRType.BARCODE_DETECTION:
          await this.initBarcodeReaderPolyFill()
          break
        default:
          await promptVideoPermission()
          await this.initZxingReader()
          break
      }
    } catch (error) {
      errorHandler(error as TError)
    } finally {
      this.isReadyToScan = true
    }
  }

  openErrorToast(message: string) {
    openMessage(message, 'error', 0)
  }

  get currentBarcodeId(): string {
    return this.$store.state.barcode?.barcode?.id
  }

  forward() {
    throw Error('This function need to implement on component')
  }

  validateBarcode(resultScanned: IBarcode, scannedBarcodeList?: IBarcode[]) {
    throw Error('This function need to implement on component ' + resultScanned)
  }

  onSocketMobileScan(scannedText: string) {
    this.handleGotScanBarcode(scannedText)
  }

  buildBarcodeLookupKey(barcodeId: string, externalId?: string): string {
    return externalId ? `${barcodeId}:${externalId}` : barcodeId
  }

  async handleGotScanBarcode(plainText: string) {
    const scannedText = plainText.trim()
    if (!scannedText || !this.componentMounted || this.isDisplayingAlert) return
    try {
      const regexBarcodeId = QRCODE_BARCODE_TYPE_REGEX
      let barcodeId = ''
      let externalId
      if (scannedText.match(regexBarcodeId)) {
        const urlParts = scannedText.split('/')
        barcodeId = urlParts.pop() || ''
        // Scanned barcode is current barcode, skip it
        if (barcodeId === this.currentBarcodeId) {
          openMessage(this.$t('barcode_already_scanned'), 'info')
          return
        }

        // Skip barcode which one already added in list
        if (this.scannedBarcodeList.find((val) => val.id === barcodeId)) {
          openMessage(this.$t('barcode_already_scanned', { id: barcodeId }), 'info')
          return
        }
      } else {
        const detectedText = scannedText || ''
        const projectCode = this.$route?.params?.project.split('@')
        if (projectCode.length > 1) projectCode.pop()

        barcodeId = `projectCode=${projectCode[0]}`
        externalId = detectedText
      }
      const barcodeLookupKey = this.buildBarcodeLookupKey(barcodeId, externalId)
      if (this.pendingRequestBarcodeIds.has(barcodeLookupKey)) {
        return
      }
      this.pendingRequestBarcodeIds.add(barcodeLookupKey)
      const resultScanned: IBarcode = await getBarcode(barcodeId, externalId).finally(() => {
        this.pendingRequestBarcodeIds.delete(barcodeLookupKey)
      })
      if (!this.componentMounted) return
      if (!resultScanned) throw this.$t('barcode does not exist')
      if (
        this.bulkUpdateOperationLimitCount &&
        this.scannedBarcodeList.length >= this.bulkUpdateOperationLimitCount.maxValue
      ) {
        throw this.$t('bulk_tracking_over_barcodes_is_not_allowed', {
          count: this.bulkUpdateOperationLimitCount.maxValue,
        })
      }

      await this.validateBarcode(resultScanned, this.scannedBarcodeList)

      // Display target barcode notifications if any.
      if (this.barcodeReaderType === EBCRType.BARCODE_DETECTION) {
        this.barcodesLoadingNotifications = { ...this.barcodesLoadingNotifications, [resultScanned.id]: true }
        checkTargetAlertNotifications(resultScanned)
          .then((notificationMsg) => {
            const loading = { ...this.barcodesLoadingNotifications }
            delete loading[resultScanned.id]
            this.barcodesLoadingNotifications = loading
            if (notificationMsg) {
              this.barcodesWithNotifications = {
                ...this.barcodesWithNotifications,
                [resultScanned.id]: notificationMsg,
              }
            }
          })
          .catch(() => {
            const loading = { ...this.barcodesLoadingNotifications }
            delete loading[resultScanned.id]
            this.barcodesLoadingNotifications = loading
          })
      } else {
        this.isDisplayingAlert = true
        try {
          await displayTargetAlert(resultScanned, () => this.playErrorSound())
        } finally {
          this.isDisplayingAlert = false
        }
      }

      this.scannedBarcode = resultScanned.id
      if (typeof this.addBarcode === 'function') {
        this.addBarcode(resultScanned.id)
      }
      if (!this.scannedBarcodeList.find((bc) => bc.id === resultScanned?.id)) {
        this.scannedBarcodeList.push(resultScanned)
        this.onNewScannedBarcode(resultScanned)

        openMessage(this.$t('barcode detected'), 'success')

        this.valueCode = ''
      }
    } catch (error) {
      this.handleScanError(error)
    } finally {
      this.valueCode = ''
    }
  }

  playErrorSound() {
    if (this.isPlayErrorSound) return
    this.isPlayErrorSound = true
    // Reset the guard only after playback finishes so rapid consecutive
    // errors don't queue up multiple (potentially infinite) plays.
    this.failScanSound.once('end', () => {
      this.isPlayErrorSound = false
    })
    this.failScanSound.play()
  }

  handleScanError(error: unknown) {
    if (!this.componentMounted) return
    this.playErrorSound()
    if (error === 'cancel') return
    let message

    // const error = err as TError
    if (typeof error === 'object' && error?.hasOwnProperty('id')) {
      message = this.$t(`errors.${(error as IAPIErrorCode)?.id?.pop()}`)
    } else if (typeof error === 'string') {
      const modelObject = this.errorVariableTemplate()
      errorHandler(error as TError, modelObject)
      return
    } else if (!isEmpty(error)) {
      message = JSON.stringify(error)
    }
    if (message && message.trim() !== '') {
      this.openErrorToast(message)
    }
  }

  // We will implement error variable template according action here
  errorVariableTemplate() {
    return {
      barcodeType: this.barcodeName,
      fTP1: this.currentTrackPointName,
      tTP2: this.nextSelectTrackingPointName,
    }
  }

  get project() {
    return this.$store.state.project.details
  }

  get barcodeType(): IBarcodeDefinitionType {
    return this.project?.barcodeTypes?.[this.barcode?.barcodeType]
  }

  get barcodeName(): string {
    return this.barcodeType?.name || ''
  }

  get currentTrackPointName() {
    return this.project?.trackPoints[this.currentTrackPointKey]?.name
  }

  get currentTrackPointKey() {
    return this.barcode?.currentTrackPointKey
  }

  onNewScannedBarcode(bc: IBarcode) {
    const found = this.barcodes.find((barcode) => barcode.id === bc.id)

    this.successScanSound.play()

    if (!found && bc?.id !== this.barcode?.id) this.barcodes.push(bc)
  }

  onNotificationIconClick(bc: IDisplayBarcodes) {
    this.currentNotificationBarcode = bc
  }

  onNotificationConfirm() {
    if (!this.currentNotificationBarcode) return
    const updated = { ...this.barcodesWithNotifications }
    delete updated[this.currentNotificationBarcode.id]
    this.barcodesWithNotifications = updated
    this.currentNotificationBarcode = null
  }

  onNotificationRemove() {
    if (!this.currentNotificationBarcode) return
    this.removeScannedBarcode(this.currentNotificationBarcode.id)
    this.currentNotificationBarcode = null
  }

  removeScannedBarcode(bcId: string) {
    this.scannedBarcodeList = this.scannedBarcodeList.filter((b: IBarcode) => b.id !== bcId)
    this.barcodes = this.barcodes.filter((b: IBarcode) => b.id !== bcId)
    const updated = { ...this.barcodesWithNotifications }
    delete updated[bcId]
    this.barcodesWithNotifications = updated
  }

  onCheckScannedBarcode() {
    this.dialogVisible = true
  }

  onCloseDialog() {
    if (this.pendingSubmit && this.hasPendingNotifications) {
      openMessage(this.$t('please_confirm_all_notifications'), 'warning')
      this.pendingSubmit = false
      this.dialogVisible = false
      return
    }
    this.dialogVisible = false
    if (this.pendingSubmit) {
      this.pendingSubmit = false
      this.isAllowToLeave = true
      this.forward()
    }
  }

  async onLeaveGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
    if (this.isAllowToLeave) {
      next()
      return
    }
    await this.$confirm(this.$t('not_save_confirm'), '', {
      confirmButtonText: this.$t('ok'),
      confirmButtonClass: 'danger',
      cancelButtonText: this.$t('cancel'),
    })
      .then(async () => {
        next()
      })
      .catch((e: TError) => {
        if (e !== 'cancel') {
          next()
        }
      })
    next(false)
  }

  get hasPendingNotifications(): boolean {
    return (
      Object.keys(this.barcodesWithNotifications).length > 0 ||
      Object.keys(this.barcodesLoadingNotifications).length > 0
    )
  }

  submit() {
    if (this.hasPendingNotifications) {
      this.pendingSubmit = true
      this.dialogVisible = true
      return
    }
    this.isAllowToLeave = true
    this.forward()
  }

  set isMultipleScan(value: boolean) {
    localStorage.setItem('[multiple-scan]_is_multiple_scan', value.toString())
    this.multipleScan = value
  }

  get isMultipleScan() {
    const storedValue = localStorage.getItem('[multiple-scan]_is_multiple_scan')
    if (!this.isShowMultipleScan) {
      localStorage.setItem('[multiple-scan]_is_multiple_scan', 'false')
      return false
    }

    if (!storedValue) {
      localStorage.setItem('[multiple-scan]_is_multiple_scan', this.multipleScan.toString())
      return this.multipleScan
    }

    this.multipleScan = storedValue.toLowerCase() === 'true'
    return this.multipleScan
  }

  handleUpdateValueCode(value: string) {
    this.valueCode = value
  }

  processValueCode(value: string) {
    let result = ''
    if (this.valueCode.trim() !== '') {
      result = this.valueCode + this.delimiter + value
    } else {
      result = value
    }
    return result.trim()
  }

  @Watch('isMultipleScan')
  onMultipleScanModeChange() {
    if (this.isMultipleScan === false && this.valueCode !== '') {
      this.$confirm(this.$t('change_camera_mode_confirm'), this.$t('Warning'), {
        confirmButtonText: this.$t('confirm'),
        confirmButtonClass: 'danger',
        cancelButtonText: this.$t('cancel'),
        closeOnClickModal: true,
        showCancelButton: true,
      })
        .then(() => {
          this.valueCode = ''
        })
        .catch(() => {
          this.isMultipleScan = true
        })
    }
  }
}
